<template>
  <div>
    <h1 class="is-size-5 padded-container" style="margin-top: 0.5rem">
      <span class="icon">
        <i class="fa-solid fa-file-import" />
      </span>
      Import {{ meta.model.label }} (Initialize)
    </h1>

    <div class="padded-container" style="margin: 1rem 0">
      <div style="display: flex; align-items: baseline; margin-bottom: 1rem">
        <div class="control" style="margin-right: 0.5rem">
          <label class="radio">
            <input v-model="runMode" type="radio" value="single-end" />
            Single-end
          </label>
          <label class="radio">
            <input v-model="runMode" type="radio" value="paired-end" />
            Paired-end
          </label>
        </div>
        <div
          class="select"
          style="margin-right: 0.5rem"
          @change="handleSelectFlowCell"
        >
          <select v-model="selectedFlowCell" :disabled="flowCells.length < 2">
            <option key="title" disabled value>Select flow cell</option>
            <option v-for="flowCell in flowCells" :key="flowCell">
              {{ flowCell }}
            </option>
          </select>
        </div>
        <div class="select">
          <select v-model="selectedLane" :disabled="lanes.length < 2">
            <option key="title" disabled value>Select lane</option>
            <option v-for="lane in lanes" :key="lane">{{ lane }}</option>
          </select>
        </div>
      </div>
      <div class="field">
        <div style="display: flex; align-items: center">
          <label class="label" style="margin-right: 0.5rem">
            Regular expression for sample name extraction
            <i
              v-tooltip="{ html: true, content: regexHelp }"
              class="fa-solid fa-info-circle"
            />
          </label>
          <div class="control" style="margin-bottom: 0.5rem">
            <div class="select">
              <select @change="handlePresetChange">
                <option value>Choose Preset</option>
                <option value="singleEnd" selected>Single-end</option>
                <option value="pairedEnd">Paired-end</option>
              </select>
            </div>
          </div>
        </div>
        <div class="control">
          <input
            v-model="sampleRegex"
            class="input is-family-monospace"
            type="text"
            placeholder="e.g. Prefix_(?<sample>.+)\.fq\.gz"
          />
        </div>
      </div>
      <div style="display: flex; align-items: center">
        <span
          v-if="hasErrors()"
          v-tooltip="{ html: true, content: errorHelp }"
          class="icon has-text-danger"
          style="margin-right: 0.25rem"
        >
          <i class="fa-solid fa-exclamation-triangle" />
        </span>
        <button
          class="button is-primary is-outlined"
          :disabled="hasErrors()"
          @click="handleSave"
        >
          <span class="icon">
            <i class="fa-solid fa-forward" />
          </span>
          <span>Continue</span>
        </button>
        <input
          v-model="assayName"
          class="input"
          type="text"
          style="max-width: 250px; margin-left: 0.5rem"
          placeholder="Assay name"
        />
        <label class="checkbox" style="margin-left: 0.5rem">
          <input v-model="multiplexed" class="checkbox" type="checkbox" />
          multiplexed
        </label>
        <label class="checkbox" style="margin-left: 0.5rem">
          <input v-model="demultiplexed" class="checkbox" type="checkbox" />
          demultiplexed
        </label>
        <SelectContainer
          v-model="selectedSequencer"
          :disabled="hasErrors()"
          style="margin-left: 0.5rem; min-width: 250px"
          label="name"
          track-by="id"
          :load-options="loadSequencers"
          placeholder="Select sequencer"
        />
        <input
          v-model="producer"
          style="margin-left: 0.5rem"
          type="text"
          class="input"
          placeholder="Producer name"
        />
      </div>
    </div>

    <div class="padded-container" style="overflow: auto">
      <table class="table is-fullwidth">
        <thead>
          <tr>
            <th>Name</th>
            <th>Sample</th>
            <th>Flow Cell</th>
            <th>Lane</th>
            <th>Folder</th>
            <th>Size</th>
          </tr>
        </thead>
        <tfoot>
          <tr>
            <th>Name</th>
            <th>Sample</th>
            <th>Flow Cell</th>
            <th>Lane</th>
            <th>Folder</th>
            <th>Size</th>
          </tr>
        </tfoot>
        <tbody>
          <tr v-for="file in displayedFiles" :key="file.name">
            <td>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <span class="is-family-monospace" v-html="name(file)"></span>
            </td>
            <td>{{ sample(file) }}</td>
            <td>{{ file.flowcell || '' }}</td>
            <td>{{ file.lane || '' }}</td>
            <td>
              <span class="is-family-monospace">{{ directory(file) }}/</span>
            </td>
            <td>{{ prettySize(file.size) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { every, sortBy, uniq, uniqBy } from 'lodash-es'
import { mapGetters } from 'vuex'
import XRegExp from 'xregexp'

import { get, post } from '@/api'
import { usePageShell } from '@/composables/usePageShell'
import { prettySize } from '@/utils'
import { escapeHtml } from '@/utils/sanitize'

import SelectContainer from '@/components/SelectContainer.vue'

const regexPresets = {
  singleEnd: `^(?<sample>.+)\\.(fq|fastq|txt)(.gz)?$`,
  pairedEnd: `^(?<sample>.+)[._]R?[12]\\.(fq|fastq|txt)(.gz)?$`,
}

export default {
  components: {
    SelectContainer,
  },
  setup() {
    usePageShell({ title: 'Initialize NGS Illumina Assay' })
  },
  props: {
    files: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      runMode: 'single-end',
      sampleRegex: regexPresets.singleEnd,
      selectedFlowCell: '',
      selectedLane: '',
      validFiles: this.files.filter((file) => file.type === 'fastq'),
      assayName: '',
      selectedSequencer: null,
      producer: '',
      multiplexed: false,
      demultiplexed: false,
    }
  },
  computed: {
    ...mapGetters('meta', ['getMeta']),
    ...mapGetters('auth', ['loggedInUser']),
    meta() {
      return this.getMeta('assays', 'assay', 'NGSILLUMINAASSAY')
    },
    flowCells() {
      return this.validFiles.length > 0
        ? uniq(
            this.validFiles
              .filter((file) => file.flowcell)
              .map((file) => file.flowcell),
          )
        : []
    },
    lanes() {
      return this.validFiles.length > 0 && this.selectedFlowCell
        ? uniq(
            this.validFiles
              .filter(
                (file) => file.lane && file.flowcell === this.selectedFlowCell,
              )
              .map((file) => file.lane),
          )
        : []
    },
    displayedFiles() {
      let ret = sortBy(this.validFiles, this.sample)
      if (this.selectedFlowCell) {
        ret = ret.filter((file) => file.flowcell === this.selectedFlowCell)
      }
      if (this.selectedLane) {
        ret = ret.filter((file) => file.lane === this.selectedLane)
      }
      return ret
    },
  },
  watch: {
    displayedFiles: {
      handler: 'setSequencer',
      immediate: true,
    },
  },
  created() {
    if (this.flowCells.length === 1) {
      this.selectedFlowCell = this.flowCells[0]
      if (this.lanes.length === 1) {
        this.selectedLane = this.lanes[0]
      }
    }
  },
  methods: {
    prettySize,
    directory(file) {
      return /.+\/(.+)\//.exec(file.uri)[1]
    },
    errorHelp() {
      return `
        <div>
          <h1>
            <span class="icon"><i class="fa-solid fa-exclamation-triangle"></i></span>
            Please fix following errors
          </h1>
          <div class="content">
            <ul>
              ${this.errors()
                .map((error) => `<li>${error}</li>`)
                .join('\n')}
            </ul>
          </div>
        </div>
      `
    },
    errors() {
      const ret = []

      const displayedFlowCells = uniq(
        this.displayedFiles.map((file) => file.flowcell),
      )
      if (displayedFlowCells.length > 1) {
        ret.push('Not a single flow cell')
      }

      const displayedLanes = uniq(this.displayedFiles.map((file) => file.lane))
      if (displayedLanes.length > 1) {
        ret.push('Not a single lane')
      }

      if (!every(this.displayedFiles, (file) => this.sample(file))) {
        ret.push('Missing sample names')
      }

      return ret
    },
    hasErrors() {
      return this.errors().length > 0
    },
    handleSave() {
      const lane = {
        id: this.selectedLane,
        name: this.assayName,
        type: this.runMode,
        multiplexed: this.multiplexed,
        demultiplexed: this.demultiplexed,
        user: this.loggedInUser.name,
        filelist: this.displayedFiles.map((f) => ({
          name: f.name,
          uri: f.uri,
          type: f.type,
          bytes: f.size,
          sample: this.sample(f),
        })),
        samples: {},
      }

      uniq(this.displayedFiles.map((f) => this.sample(f))).forEach((sample) => {
        lane.samples[sample] = {}
      })

      const payload = {
        managed: false,
        data: [
          {
            flowcell: this.selectedFlowCell,
            lanes: [lane],
          },
        ],
      }

      if (this.selectedSequencer) {
        payload.data[0].sequencer = this.selectedSequencer
      }

      if (this.producer) {
        payload.data[0].producer = this.producer
      }
      post('assays/assays/ngsilluminaassay/initialize/', { data: payload })
        .then(() => {
          // FIXME properly handle this => redirect to verify page
        })
        .catch((error) => {
          const errorMessage = error.response.data.message
          this.$store.commit('notify', {
            type: 'error',
            message: errorMessage,
          })
        })
    },
    handleSelectFlowCell() {
      if (this.lanes.length === 1) {
        this.selectedLane = this.lanes[0]
      } else {
        this.selectedLane = ''
      }
    },
    handlePresetChange(event) {
      const name = event.target.value
      if (name) {
        this.sampleRegex = regexPresets[name]
      } else {
        this.sampleRegex = ''
      }
    },
    loadSequencers() {
      const path =
        'stocks/equipment/?format=json&type=sequencer&page_size=max&response_format=flat&fields=name,id'
      return get(path)
    },
    name(file) {
      if (this.sampleRegex) {
        try {
          const re = new XRegExp(this.sampleRegex)
          const match = XRegExp.exec(file.name, re)
          if (match.sample) {
            const index = file.name.search(match.sample)
            const prefix = file.name.substr(0, index)
            const suffix = file.name.substr(index + match.sample.length)
            // Filename segments are user-controlled — escape each before wrapping
            // in our own highlight markup (rendered via v-html).
            let html = ''
            if (prefix) {
              html += `<span>${escapeHtml(prefix)}</span>`
            }
            html += `<span class="has-text-primary has-text-weight-bold">${escapeHtml(match.sample)}</span>`
            if (suffix) {
              html += `<span class="has-text-danger">${escapeHtml(suffix)}</span>`
            }
            return html
          } else {
            return escapeHtml(file.name)
          }
        } catch {
          return escapeHtml(file.name)
        }
      } else {
        return escapeHtml(file.name)
      }
    },
    regexHelp() {
      return `
      <div class="has-text-centered">
        <p>You can provide a regular expression to extract sample names from the file names.</p>
        <p>You can choose a preset from the dropdown.</p>
        <p>If you write a custom expression, you need to define a named capture group: <span class="is-family-monospace has-text-weight-bold">(?&lang;sample&rang;)</span>.</p>
        <p>
          The expression
          <span class="is-family-monospace has-text-weight-bold">ProjectX_(?&lang;sample&rang;.+)_[12]\\.fq.gz</span>, for example, would match the sample
          <span class="is-family-monospace has-text-weight-bold">sampleX</span> for files
          <span class="is-family-monospace has-text-weight-bold">ProjectX_sampleX_1.fq.gz<span> and
          <span class="is-family-monospace has-text-weight-bold">ProjectX_sampleX_2.fq.gz<span>.
        </p>
      </div>
      `
    },
    sample(file) {
      if (this.sampleRegex) {
        try {
          const re = new XRegExp(this.sampleRegex)
          const match = XRegExp.exec(file.name, re)
          return match.sample || ''
        } catch {
          return ''
        }
      } else {
        return file.sample
      }
    },
    setSequencer() {
      const sequencers = uniqBy(
        this.displayedFiles
          .filter((f) => f.sequencer && f.sequencer.id && f.sequencer.name)
          .map((f) => f.sequencer),
        'id',
      )
      if (sequencers.length === 1) {
        this.selectedSequencer = sequencers[0]
      }
    },
  },
}
</script>

<style scoped>
h1 {
  font-family: var(--font-heading);
}
</style>
